/**
 * Every notification type this codebase emits, and the entity kinds they point at.
 *
 * Producers, copy renderers and the per-app route resolvers all key off these strings, so
 * a typo is a compile error in exactly one of the three rather than a card that silently
 * renders the "Ada pembaruan untukmu." fallback or navigates nowhere.
 *
 * The values are still plain strings on the wire: `notification_event.type` and
 * `.entityType` are `text` columns precisely so a new kind needs no enum migration, and
 * every consumer must keep degrading gracefully on a value it does not recognise.
 */
export const NOTIFICATION_TYPE = {
  /** A book/collection recommendation. The phase-1 type; predates this table. */
  CONTENT_SHARED: 'content.shared',

  // --- Tugas (Course), student side ---
  /** A teacher enrolled this child in a new course. */
  ASSIGNMENT_ASSIGNED: 'assignment.assigned',
  /** A teacher graded this child's in-class work. Rolled up per course, per Jakarta day. */
  ASSIGNMENT_GRADED: 'assignment.graded',
  /** The course deadline is within a day and this child has not finished. */
  ASSIGNMENT_DUE_SOON: 'assignment.due_soon',

  // --- Tugas (Course), teacher side. All roll-ups: one card, a climbing count. ---
  /** Work is waiting to be graded. Rolled up per course, per Jakarta day. */
  ASSIGNMENT_NEEDS_GRADING: 'assignment.needs_grading',
  /** Students who never opened the course. Emitted once per course, ever. */
  ASSIGNMENT_NOT_STARTED: 'assignment.not_started',
  /** Students still unfinished with the deadline a day out. Once per course deadline. */
  ASSIGNMENT_INCOMPLETE: 'assignment.incomplete',
  /** Everyone finished. Emitted once per course, ever. */
  ASSIGNMENT_ALL_COMPLETED: 'assignment.all_completed',

  // --- Tantangan (Challenge), student side ---
  /** This child was auto-enrolled into a challenge their school or class was given. */
  CHALLENGE_ASSIGNED: 'challenge.assigned',
  /** The challenge deadline is within a day and this child has not finished. */
  CHALLENGE_DUE_SOON: 'challenge.due_soon',

  // --- Cermin orang tua: what the child was told, retold to their parent ---
  //
  // A connected child's parent has to see the school news their child gets, and cannot be
  // added as an extra recipient on the child's own event to get it: that copy is written in
  // the second person ("tugas baru buat kamu", "hasil kerja kamu"), and `(eventId, userId,
  // audience)` is unique, so a parent with two children in one class would receive one card
  // for two children. Each of these is therefore its own event, one per (parent, child),
  // with third-person copy that names the child.
  /** A teacher enrolled this parent's child in a new course. */
  GUARDIAN_ASSIGNMENT_ASSIGNED: 'guardian.assignment_assigned',
  /** A teacher graded this parent's child's work. Rolled up per course, per Jakarta day. */
  GUARDIAN_ASSIGNMENT_GRADED: 'guardian.assignment_graded',
  /** This parent's child has a course deadline within a day and has not finished. */
  GUARDIAN_ASSIGNMENT_DUE_SOON: 'guardian.assignment_due_soon',
  /** This parent's child was enrolled into a challenge their school or class was given. */
  GUARDIAN_CHALLENGE_ASSIGNED: 'guardian.challenge_assigned',
  /** This parent's child has a challenge deadline within a day and has not finished. */
  GUARDIAN_CHALLENGE_DUE_SOON: 'guardian.challenge_due_soon',
  /** Someone recommended a book, collection or challenge to this parent's child. */
  GUARDIAN_CONTENT_SHARED: 'guardian.content_shared',
} as const

export type NotificationType = (typeof NOTIFICATION_TYPE)[keyof typeof NOTIFICATION_TYPE]

/**
 * The `guardian.*` half of {@link NOTIFICATION_TYPE}, as a type of its own.
 *
 * The mirror helper accepts nothing else: a guardian event whose type is not in this set would
 * render the "Ada pembaruan untukmu." fallback in a parent's feed and route nowhere.
 */
export type GuardianNotificationType =
  | typeof NOTIFICATION_TYPE.GUARDIAN_ASSIGNMENT_ASSIGNED
  | typeof NOTIFICATION_TYPE.GUARDIAN_ASSIGNMENT_GRADED
  | typeof NOTIFICATION_TYPE.GUARDIAN_ASSIGNMENT_DUE_SOON
  | typeof NOTIFICATION_TYPE.GUARDIAN_CHALLENGE_ASSIGNED
  | typeof NOTIFICATION_TYPE.GUARDIAN_CHALLENGE_DUE_SOON
  | typeof NOTIFICATION_TYPE.GUARDIAN_CONTENT_SHARED

/**
 * `notification_event.entityType` values.
 *
 * `COURSE` is notification-only — a `Course` is a teacher's assignment, which nothing in
 * `share_entity_type_enum` describes. `CHALLENGE` is the same `Challenge` row the share
 * flow already points at, so it deliberately reuses that spelling rather than inventing a
 * second name for one table.
 */
export const NOTIFICATION_ENTITY = {
  COURSE: 'COURSE',
  CHALLENGE: 'CHALLENGE',
} as const

/**
 * Types whose reader is the teacher/parent who set the work, not the child doing it.
 *
 * The audience column cannot answer this: these are all `RECIPIENT` rows (the teacher
 * genuinely receives them), and `ACTOR` means "your own send", which a cron-generated
 * roll-up never is. Route resolvers use this to send a teacher to the grading screen and
 * a child to the mission screen for the very same `COURSE` entity.
 */
const TEACHER_FACING: ReadonlySet<string> = new Set([
  NOTIFICATION_TYPE.ASSIGNMENT_NEEDS_GRADING,
  NOTIFICATION_TYPE.ASSIGNMENT_NOT_STARTED,
  NOTIFICATION_TYPE.ASSIGNMENT_INCOMPLETE,
  NOTIFICATION_TYPE.ASSIGNMENT_ALL_COMPLETED,
])

export function isTeacherFacingNotification(type: string): boolean {
  return TEACHER_FACING.has(type)
}

/**
 * Types whose reader is a connected child's parent, watching from the family side.
 *
 * Same problem the teacher-facing set solves, one step further out: these are `RECIPIENT`
 * rows on `ASSIGNMENT`/`SHARE` events pointing at a `COURSE` or a `CHALLENGE`, which is
 * exactly what a child's own card looks like. The card renderer needs it to draw the
 * child's avatar rather than the teacher's, and the route resolvers need it to send a
 * parent to their child's profile instead of to a learning screen only the child can open.
 */
const GUARDIAN_FACING: ReadonlySet<string> = new Set([
  NOTIFICATION_TYPE.GUARDIAN_ASSIGNMENT_ASSIGNED,
  NOTIFICATION_TYPE.GUARDIAN_ASSIGNMENT_GRADED,
  NOTIFICATION_TYPE.GUARDIAN_ASSIGNMENT_DUE_SOON,
  NOTIFICATION_TYPE.GUARDIAN_CHALLENGE_ASSIGNED,
  NOTIFICATION_TYPE.GUARDIAN_CHALLENGE_DUE_SOON,
  NOTIFICATION_TYPE.GUARDIAN_CONTENT_SHARED,
])

export function isGuardianNotification(type: string): boolean {
  return GUARDIAN_FACING.has(type)
}
